import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dropdown } from "primereact/dropdown";
import { tempCategories } from "../../utils/templateUtils";
import { CustomButton } from "../CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import StartNodeCheckMark from "../../view/Bots/components/start-node-checkmark";
import { classNames } from "primereact/utils";
import { Controller, useForm } from "react-hook-form";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import FileInputComponent from "../../view/Bots/components/FileInput";
import {
  selectActiveBotFlowState,
  useActiveBotFlowActions,
} from "../../hooks/useActiveBotFlow";
import { useUtility } from "../../context/UtilityContext";
import { GET_TEMPLATES } from "../../api/wca/wca.template.services";

const MEDIA_VARIABLE_KEY = "{{mediaUrl}}";
const TEMPLATE_MEDIA_TYPES = {
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document",
};

const normalizeSavedVariables = (savedVariables = {}) => {
  if (Array.isArray(savedVariables)) {
    return savedVariables.reduce((acc, item) => ({ ...acc, ...item }), {});
  }

  return savedVariables && typeof savedVariables === "object" ? { ...savedVariables } : {};
};

const normalizeTemplateMediaVariable = (mediaValue, templateType = "TEXT") => {
  if (!mediaValue) {
    return null;
  }

  const mediaKey = TEMPLATE_MEDIA_TYPES[String(templateType || "TEXT").toUpperCase()];
  if (!mediaKey) {
    return mediaValue;
  }

  if (typeof mediaValue === "string") {
    return { [mediaKey]: mediaValue };
  }

  if (typeof mediaValue === "object") {
    if (mediaValue.image || mediaValue.video || mediaValue.document) {
      return mediaValue;
    }

    if (mediaValue.url) {
      return { [mediaKey]: mediaValue.url };
    }
  }

  return null;
};

const normalizeTemplateVariables = (variables = {}, templateType = "TEXT") => {
  const normalizedVariables = normalizeSavedVariables(variables);

  if (Object.prototype.hasOwnProperty.call(normalizedVariables, MEDIA_VARIABLE_KEY)) {
    const normalizedMediaVariable = normalizeTemplateMediaVariable(
      normalizedVariables[MEDIA_VARIABLE_KEY],
      templateType,
    );

    if (normalizedMediaVariable) {
      normalizedVariables[MEDIA_VARIABLE_KEY] = normalizedMediaVariable;
    } else {
      delete normalizedVariables[MEDIA_VARIABLE_KEY];
    }
  }

  return normalizedVariables;
};

const TemplateNodeInputWCA = ({ onCancel = () => {}, onSave = () => {} }) => {
  const [templateList, setTemplateList] = useState([]);
  const [selecteTemplate, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState();
  const { selectedNode } = useSelector(selectActiveBotFlowState);
  const { updateNodePayload } = useActiveBotFlowActions();
  const { selectedMetadataId } = useUtility();

  const dispatch = useDispatch();
  const defaultValues = {};

  const { control, setValue } = useForm({ defaultValues });

  const [tempVariables, setTempvariables] = useState({});
  const [variables, setVariables] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);

  const hydratedNodeIdRef = useRef(null);
  const lastFetchKeyRef = useRef(null);

  const getTemplateIdentity = useCallback((template) => template?.templateId ?? template?.id ?? null, []);
  const getTemplateName = useCallback((template) => template?.elementName ?? template?.name ?? null, []);
  const getTemplateRows = useCallback((response) => response?.rows || [], []);
  const safeParseJson = useCallback((value, fallback = {}) => {
    if (!value || typeof value !== "string") return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }, []);
  const getTemplateButtonsFromComponents = useCallback((components = []) => {
    const buttonComponent = components.find((component) => component?.type === "BUTTONS");
    return buttonComponent?.buttons || [];
  }, []);
  const getTemplateHeaderFromComponents = useCallback((components = []) => {
    const headerComponent = components.find((component) => component?.type === "HEADER");
    return headerComponent?.text || "";
  }, []);
  const getTemplateBodyFromComponents = useCallback((components = []) => {
    const bodyComponent = components.find((component) => component?.type === "BODY");
    return bodyComponent?.text || "";
  }, []);
  const getTemplateType = useCallback((template = {}, parsedMeta = {}) => {
    return (
      template?.templateType ||
      parsedMeta?.templateType ||
      template?.components?.find((component) => component?.type === "HEADER")?.format ||
      "TEXT"
    );
  }, []);
  const getTemplatePayload = useCallback((template = {}) => {
    const parsedMeta = safeParseJson(template?.containerMeta, null);

    if (parsedMeta) {
      return {
        header: parsedMeta?.header || "",
        data: parsedMeta?.data || "",
        buttons: parsedMeta?.buttons || [],
        templateType: getTemplateType(template, parsedMeta),
      };
    }

    return {
      header: getTemplateHeaderFromComponents(template?.components || []),
      data: getTemplateBodyFromComponents(template?.components || []),
      buttons: getTemplateButtonsFromComponents(template?.components || []),
      templateType: getTemplateType(template),
    };
  }, [
    getTemplateBodyFromComponents,
    getTemplateButtonsFromComponents,
    getTemplateHeaderFromComponents,
    getTemplateType,
    safeParseJson,
  ]);
  const normalizeTemplate = useCallback((template) => ({
    ...template,
    elementName: getTemplateName(template),
    templateType: getTemplateType(template),
  }), [getTemplateName, getTemplateType]);

  const buildVariableListFromSavedValues = useCallback((savedVariables = {}) => {
    return Object.entries(savedVariables)
      .filter(([variableName]) => variableName !== MEDIA_VARIABLE_KEY)
      .map(([variableName, variableValue]) => ({
        variableName,
        variableValue,
      }));
  }, []);

  const normalizeSavedNodeTemplateData = useCallback((nodeData = {}) => {
    const savedVariables = normalizeTemplateVariables(
      normalizeSavedVariables(nodeData?.variables),
      nodeData?.templateType || "TEXT",
    );

    return {
      templateId: nodeData?.templateId ?? null,
      templateName: nodeData?.templateName || "Saved Template",
      templateType: nodeData?.templateType || "TEXT",
      category: nodeData?.category || null,
      content: {
        header: nodeData?.content?.header || "",
        data: nodeData?.content?.data || "",
      },
      variables: savedVariables,
      options: Array.isArray(nodeData?.options) ? nodeData.options : [],
    };
  }, []);

  const areObjectsEqual = useCallback((first = {}, second = {}) => {
    const firstKeys = Object.keys(first || {});
    const secondKeys = Object.keys(second || {});

    if (firstKeys.length !== secondKeys.length) {
      return false;
    }

    return firstKeys.every((key) => JSON.stringify(first[key]) === JSON.stringify(second[key]));
  }, []);

  const createFallbackTemplate = useCallback((savedNodeData) => (
    normalizeTemplate({
      templateId: savedNodeData.templateId,
      id: savedNodeData.templateId,
      elementName: savedNodeData.templateName,
      name: savedNodeData.templateName,
      templateType: savedNodeData.templateType,
      category: savedNodeData.category,
      variables: buildVariableListFromSavedValues(savedNodeData.variables),
      containerMeta: JSON.stringify({
        header: savedNodeData.content.header,
        data: savedNodeData.content.data,
        buttons: savedNodeData.options || [],
        templateType: savedNodeData.templateType,
      }),
    })
  ), [buildVariableListFromSavedValues, normalizeTemplate]);

  const applyTemplateEditorState = useCallback(({ savedNodeData, catalogTemplate = null, templateOptions = [] }) => {
    const templateForSelection =
      catalogTemplate || (savedNodeData?.templateId ? createFallbackTemplate(savedNodeData) : null);

    const fetchedPayload = catalogTemplate ? getTemplatePayload(catalogTemplate) : {};
    const mergedVariables = savedNodeData?.variables || {};
    const mergedVariableList =
      (catalogTemplate?.variables || []).length > 0
        ? (catalogTemplate.variables || []).map((item) => ({
            ...item,
            variableValue: mergedVariables[item.variableName] ?? item.variableValue ?? "",
          }))
        : buildVariableListFromSavedValues(mergedVariables);

    const mergedContent = {
      header: savedNodeData?.content?.header || fetchedPayload?.header || "",
      data: savedNodeData?.content?.data || fetchedPayload?.data || "",
    };
    const resolvedTemplateType =
      savedNodeData?.templateType || catalogTemplate?.templateType || fetchedPayload?.templateType || "TEXT";
    const normalizedMediaFile = normalizeTemplateMediaVariable(
      mergedVariables[MEDIA_VARIABLE_KEY],
      resolvedTemplateType,
    );

    setTemplateList(templateOptions);
    setTemplate(templateForSelection);
    setVariables(mergedVariableList);
    setTempvariables(mergedVariables);
    setMessage(mergedContent.data);
    setMediaFile(normalizedMediaFile);
    setValue("message", mergedContent.data, { shouldValidate: true });

    mergedVariableList.forEach((item) => {
      setValue(item.variableName, mergedVariables[item.variableName] ?? item.variableValue ?? "", {
        shouldValidate: true,
      });
    });
  }, [buildVariableListFromSavedValues, createFallbackTemplate, getTemplatePayload, setValue]);

  const resetEditorState = useCallback(() => {
    setTemplate(null);
    setTemplateList([]);
    setVariables([]);
    setTempvariables({});
    setMessage(undefined);
    setMediaFile(null);
    setValue("message", "", { shouldValidate: false });
  }, [setValue]);

  const fetchNextTemplates = useCallback(async (currentCategory, currentNode = selectedNode) => {
    const savedNodeData = normalizeSavedNodeTemplateData(currentNode?.data || {});
    const fetchKey = `${selectedMetadataId || "no-metadata"}::${currentNode?.id || "no-node"}::${currentCategory || "all"}`;

    if (!selectedMetadataId) {
      lastFetchKeyRef.current = null;
      resetEditorState();
      return;
    }

    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    lastFetchKeyRef.current = fetchKey;

    try {
      setLoading(true);
      const response = await GET_TEMPLATES(selectedMetadataId, 0, 100000);
      const rows = getTemplateRows(response);
      const approvedTemplates = rows.filter((item) => item.status === "APPROVED");
      const filteredTemplates = currentCategory
        ? approvedTemplates.filter((item) => item?.category === currentCategory)
        : approvedTemplates;

      const normalizedList = filteredTemplates.map(normalizeTemplate);
      const matchedTemplate = normalizedList.find(
        (item) => String(getTemplateIdentity(item)) === String(savedNodeData?.templateId),
      );

      if (savedNodeData?.templateId) {
        const hydratedOptions = matchedTemplate
          ? normalizedList
          : [createFallbackTemplate(savedNodeData), ...normalizedList];

        applyTemplateEditorState({
          savedNodeData,
          catalogTemplate: matchedTemplate || null,
          templateOptions: hydratedOptions,
        });
      } else {
        setTemplateList(normalizedList);
      }

      setLoading(false);
    } catch (err) {
      lastFetchKeyRef.current = null;
      setLoading(false);

      if (savedNodeData?.templateId) {
        const fallbackTemplate = createFallbackTemplate(savedNodeData);
        applyTemplateEditorState({
          savedNodeData,
          catalogTemplate: null,
          templateOptions: [fallbackTemplate],
        });
      }
    }
  }, [
    applyTemplateEditorState,
    createFallbackTemplate,
    getTemplateIdentity,
    getTemplateRows,
    normalizeSavedNodeTemplateData,
    normalizeTemplate,
    resetEditorState,
    selectedMetadataId,
    selectedNode,
  ]);

  const handleTemplateSelect = (templateData) => {
    setTemplate(templateData);

    let templateNodeData = {
      templateId: null,
      templateName: null,
      options: [],
      variables: [],
      content: {
        header: "",
        data: "",
      },
    };

    if (templateData) {
      const { variables: templateVariables } = templateData;
      const templateId = getTemplateIdentity(templateData);
      const templateName = getTemplateName(templateData);
      const { data, buttons, header, templateType } = getTemplatePayload(templateData);

      templateNodeData = {
        ...templateNodeData,
        templateId,
        category: templateData?.category || null,
        content: {
          ...templateNodeData.content,
          header,
          data,
        },
        options: buttons?.map((item) => ({
          id: uuidv4(),
          title: item.text || "",
          postbackText: "",
          type: item.type || "text",
          ...(item.url && { url: item.url }),
          ...(item.phone_number && { phoneNumber: item.phone_number }),
        })),
        variables: templateVariables?.map((item) => ({
          [item.variableName]: item.variableValue,
        })),
        templateName,
        templateType,
      };

      let tempVariable = {};
      templateData?.variables?.forEach((x) => {
        tempVariable[x.variableName] = x.variableValue;
        setValue(x.variableName, x.variableValue, { shouldValidate: true });
      });

      setValue("message", data, { shouldValidate: true });
      setMessage(data);
      setTempvariables({ ...tempVariable });
      setVariables(templateVariables && [...templateVariables]);
    }

    dispatch(
      updateNodePayload({
        selectedNodeId: selectedNode?.id,
        updatedData: { ...templateNodeData },
      }),
    );
  };

  useEffect(() => {
    if (!selectedNode?.id) {
      hydratedNodeIdRef.current = null;
      lastFetchKeyRef.current = null;
      setCategory(null);
      resetEditorState();
      return;
    }

    const nodeChanged = hydratedNodeIdRef.current !== selectedNode.id;
    const savedNodeData = normalizeSavedNodeTemplateData(selectedNode.data || {});

    if (nodeChanged) {
      hydratedNodeIdRef.current = selectedNode.id;
      resetEditorState();
      setCategory(savedNodeData.category || null);
      fetchNextTemplates(savedNodeData.category || null, selectedNode);
      return;
    }

    fetchNextTemplates(category, selectedNode);
  }, [
    category,
    fetchNextTemplates,
    normalizeSavedNodeTemplateData,
    resetEditorState,
    selectedMetadataId,
    selectedNode,
  ]);

  const handleEditVariable = (e, variableName) => {
    let currVariableData = { ...tempVariables };
    currVariableData[variableName] = `${e.target.value}`;
    setTempvariables({ ...currVariableData });
  };

  useEffect(() => {
    if (!selecteTemplate) return;
    const { data } = getTemplatePayload(selecteTemplate);

    if (!message) {
      setValue("message", data, { shouldValidate: true });
      setMessage(data);
    }
  }, [getTemplatePayload, message, selecteTemplate, setValue]);

  useEffect(() => {
    if (!selectedNode?.id) {
      return;
    }

    const nextVariables = {
      ...tempVariables,
      ...(mediaFile ? { [MEDIA_VARIABLE_KEY]: mediaFile } : {}),
    };
    const currentVariables = normalizeTemplateVariables(
      normalizeSavedVariables(selectedNode?.data?.variables),
      selectedNode?.data?.templateType || "TEXT",
    );
    const normalizedNextVariables = normalizeTemplateVariables(
      nextVariables,
      selectedNode?.data?.templateType || "TEXT",
    );

    if (areObjectsEqual(currentVariables, normalizedNextVariables)) {
      return;
    }

    dispatch(
      updateNodePayload({
        selectedNodeId: selectedNode?.id,
        updatedData: {
          variables: normalizedNextVariables,
        },
      }),
    );
  }, [areObjectsEqual, dispatch, mediaFile, selectedNode, tempVariables, updateNodePayload]);

  function templateMsg() {
    return (
      <form className="p-fluid w-full" encType="multipart/form-data">
        <div className="field mt-2 w-full">
          <label htmlFor="message">Body *</label>
          <Controller
            name="message"
            control={control}
            rules={{ required: "message is required." }}
            render={({ field, fieldState }) => (
              <InputTextarea
                className={classNames({
                  "p-invalid": fieldState.invalid,
                })}
                id={field.name}
                value={message}
                rows={5}
                cols={30}
                maxLength={1028}
                disabled
                placeholder="Enter your message (max 1028 chars)"
              />
            )}
          />

          <div className="my-2">
            {variables?.map((item, i) => (
              <div className="flex gap-2 justify-content-center align-items-center my-3" key={i}>
                <div className="w-3">
                  {i === 0 && <label>Variable</label>}
                  <InputText value={item.variableName} disabled />
                </div>
                <div className="relative flex-grow-1">
                  {i === 0 && <label>Value</label>}
                  <div className="flex align-items-center gap-2">
                    <Controller
                      rules={{ required: true }}
                      id={item.variableName}
                      name={item.variableName}
                      control={control}
                      render={({ field }) => (
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={tempVariables[item.variableName]}
                          placeholder="Enter value"
                          onChange={(e) => {
                            handleEditVariable(e, item.variableName);
                            field.onChange(e.target.value);
                          }}
                          required
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </form>
    );
  }

  const handleOptionDisable = () => {
    let isDisbale = false;
    const { options } = selectedNode.data;
    if (options && options.length !== 0) {
      for (const op of options) {
        if (op.postbackText !== "") {
          isDisbale = true;
        }
      }
    }

    return isDisbale;
  };

  return (
    <>
      <div className="m-2">
        <Dropdown
          value={category}
          onChange={(e) => setCategory(e.value)}
          options={tempCategories}
          optionLabel="key"
          showClear
          placeholder="Select Category"
          className="w-full mb-2"
        />
        <Dropdown
          value={selecteTemplate}
          options={templateList}
          onChange={(e) => handleTemplateSelect(e.value)}
          optionLabel="elementName"
          placeholder="Choose Template"
          className="w-full capitalize"
          loading={loading}
          filter
          disabled={handleOptionDisable()}
        />
        {selecteTemplate && templateMsg()}
      </div>
      {["IMAGE", "VIDEO", "DOCUMENT"].includes(selecteTemplate?.templateType) && (
        <FileInputComponent
          setMediaFile={setMediaFile}
          mediaFile={mediaFile}
          template={selecteTemplate}
        />
      )}
      <StartNodeCheckMark />
      <div className=" mt-6 mb-3 flex justify-content-end gap-3 align-items-center">
        <CustomButton varient="outline" label={"Cancel"} onClick={onCancel} />
        <CustomButton varient="filled" label={"Save"} onClick={onSave} />
      </div>
    </>
  );
};

export default TemplateNodeInputWCA;
